import { useJwtStore } from '@/stores'
import axios, { type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import type { ZodSchema } from 'zod'
import { getActiveRouter } from '@/router'
import { routeRequiresAuth } from '@/utils/authPaths'
import { loginRouteWithRedirect } from '@/utils/loginRedirect'

// Track if interceptors have been set up to avoid duplicates
let interceptorsSetup = false

// Custom property to track retry attempts
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retryAttempt?: boolean
}

export const useAxios = <RequestData = never, ResponseData = RequestData>(
  method: 'GET' | 'POST' | 'DELETE' | 'HEAD' | 'OPTIONS' | 'PUT' | 'PATCH',
  url: string,
  setAuth: boolean,
  data?: RequestData,
  // Optional Zod schema to validate the response body against
  responseSchema?: ZodSchema<ResponseData>
): Promise<AxiosResponse<ResponseData>> => {
  const config: AxiosRequestConfig = {
    baseURL: import.meta.env.VITE_BASE_URL,
    url,
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8'
    },
    timeout: 30_000
  }

  if (data) {
    config.data = data
  }

  // Only set up interceptors once to avoid duplicates
  if (!interceptorsSetup) {
    interceptorsSetup = true

    axios.interceptors.request.use(
      (config) => {
        // Auth header is set per-request below, not here
        return config
      },
      null,
      { synchronous: true }
    )

    axios.interceptors.response.use(
      (response) => {
        return response
      },
      async (error) => {
        const originalRequest = error.config as RetryableConfig

        // Handle 401 responses - try to refresh token first. A 401 that names
        // a wrong verification code refused the code, not the session, and
        // retrying it would spend a second of the code's few attempts.
        const router = getActiveRouter()
        if (
          error?.response?.status === 401 &&
          error.response.data?.error !== 'invalid_code' &&
          originalRequest &&
          router
        ) {
          const currentPath = router.currentRoute.value.path

          // Only attempt refresh on a session-authenticated page, and only
          // once. The consent screen is one of those pages: a 401 there used to
          // fall straight through, so a token that expired while the customer
          // was reading the scopes lost the whole authorization request.
          if (routeRequiresAuth(currentPath) && !originalRequest._retryAttempt) {
            const jwtStore = useJwtStore()

            // Skip refresh attempt for the refresh endpoint itself
            if (originalRequest.url === '/account/refresh') {
              jwtStore.removeJwt()
              router.push(loginRouteWithRedirect(router.currentRoute.value.fullPath))
              return Promise.reject(error)
            }

            // refreshToken() dedups concurrent callers onto one request and
            // hands each the real result, so a 401 arriving mid-refresh waits
            // for it instead of falling through to the logout below.
            if (jwtStore.getJwt) {
              const refreshed = await jwtStore.refreshToken()

              if (refreshed) {
                // Mark this request as a retry attempt
                originalRequest._retryAttempt = true
                // Update the authorization header with the new token
                originalRequest.headers.Authorization = `Bearer ${jwtStore.getJwt}`
                // Retry the original request
                return axios(originalRequest)
              }
            }

            // Refresh failed or no token - redirect to login
            jwtStore.removeJwt()
            router.push(loginRouteWithRedirect(router.currentRoute.value.fullPath))
          }
        }
        // Reject the promise so .catch() handlers are triggered
        return Promise.reject(error)
      }
    )
  }

  // Set auth header for this specific request
  if (setAuth) {
    const jwtStore = useJwtStore()
    const localJwt = jwtStore.getJwt

    if (localJwt !== null) {
      config.headers = config.headers || {}
      config.headers.Authorization = `Bearer ${localJwt}`
    }
  }

  return axios<ResponseData>(config).then((res) => {
    if (responseSchema) {
      const parsed = responseSchema.safeParse(res.data)
      if (!parsed.success) {
        const err = new Error('Invalid response payload') as Error & { issues?: unknown }
        err.name = 'SchemaValidationError'
        err.issues = parsed.error.issues
        throw err
      }
    }
    return res
  })
}
